import React from "react";
import { ChevronLeft, ChevronRight, CheckCircle2, CircleDashed, Clock } from "lucide-react";
import { addDays, format, parseISO, differenceInCalendarDays } from "date-fns";
import { mdy, mdyShort } from "@/lib/dateFmt";
import { Button } from "@/components/ui/button";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";

function anchorFromId(id) {
  return parseISO(id.replace("pp-", ""));
}
function idFromAnchor(d) {
  return `pp-${format(d, "yyyy-MM-dd")}`;
}
function labelForAnchor(d) {
  const end = addDays(d, 13);
  return `${mdy(d)} – ${mdy(end)}`;
}

/** Small chip + grey due caption shown inside each SelectItem. */
function OptionMeta({ p }) {
  const due = p.due_to_post ? parseISO(p.due_to_post) : null;
  const daysLeft = due ? differenceInCalendarDays(due, new Date()) : null;
  const publishedPill = p.published ? (
    <span className="inline-flex items-center gap-0.5 rounded-full border border-emerald-300 bg-emerald-50 text-emerald-800 text-[9px] font-black uppercase tracking-widest px-1.5 py-[1px]">
      <CheckCircle2 size={9} /> Published
    </span>
  ) : (
    <span className="inline-flex items-center gap-0.5 rounded-full border border-slate-300 bg-slate-50 text-slate-600 text-[9px] font-black uppercase tracking-widest px-1.5 py-[1px]">
      <CircleDashed size={9} /> Not Published
    </span>
  );
  return (
    <div className="flex items-center gap-1.5 flex-wrap">
      {publishedPill}
      {!p.published && due && (
        <span className="text-[10px] text-slate-400 tabular-nums">
          (Due to post: {mdyShort(due)}
          {daysLeft != null && (
            <>, {daysLeft < 0 ? `${-daysLeft}d overdue` : daysLeft === 0 ? "due today" : `${daysLeft}d left`}</>
          )})
        </span>
      )}
    </div>
  );
}

export default function PayPeriodSelector({ periods, value, onChange, published }) {
  if (!value) return null;
  const currentAnchor = anchorFromId(value);
  const prev = () => onChange(idFromAnchor(addDays(currentAnchor, -14)));
  const next = () => onChange(idFromAnchor(addDays(currentAnchor, 14)));

  const inList = periods.some((p) => p.id === value);
  const options = inList
    ? periods
    : [{ id: value, label: labelForAnchor(currentAnchor), published: !!published, is_current: false }, ...periods];

  return (
    <div className="flex items-center gap-1 sm:gap-2" data-testid="pay-period-selector">
      <Button variant="outline" size="icon" className="rounded-lg hidden sm:inline-flex" onClick={prev} data-testid="pp-prev" aria-label="Previous pay period">
        <ChevronLeft size={16} />
      </Button>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger
          className="w-[160px] sm:w-[280px] md:w-[360px] h-auto min-h-[40px] sm:min-h-[44px] py-1.5 rounded-lg [&>span]:!line-clamp-none [&>span]:w-full"
          data-testid="pp-select"
        >
          <SelectValue placeholder="Select pay period" />
        </SelectTrigger>
        <SelectContent className="max-h-[440px]">
          {options.map((p) => (
            <SelectItem key={p.id} value={p.id} data-testid={`pp-option-${p.id}`}>
              <div className="flex flex-col gap-0.5 w-full min-w-0">
                <div className="flex items-center gap-2 min-w-0">
                  <span className="font-medium truncate">{p.label}</span>
                  {p.is_current && <span className="text-[10px] uppercase tracking-widest text-blue-600 font-bold shrink-0">Current</span>}
                </div>
                <OptionMeta p={p} />
              </div>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Button variant="outline" size="icon" className="rounded-lg hidden sm:inline-flex" onClick={next} data-testid="pp-next" aria-label="Next pay period">
        <ChevronRight size={16} />
      </Button>
      <div className="ml-2 text-xs px-2 py-1 rounded-md border tabular-nums hidden sm:block"
           data-testid="pp-status"
           style={{
              background: published ? "#DCFCE7" : "#F1F5F9",
              color: published ? "#166534" : "#475569",
              borderColor: published ? "#86EFAC" : "#CBD5E1",
           }}>
        {published ? "Published" : "Draft"}
      </div>
    </div>
  );
}
